import React, { useCallback, useEffect, useState } from 'react';
import { Card, CardContent } from '../components/ui/Card';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { EmptyState } from '../components/ui/EmptyState';
import { PeriodSelector } from '../components/ui/PeriodSelector';
import { formatBRL } from '../utils/FormatDecimal';
import {
  getDREReport,
  getBalanceSheet,
  getTrialBalance,
  downloadReportPDF,
  getDominioMapping,
  getDominioExportPreview,
  downloadDominioExport,
  updateDominioMapping,
  DREReportResponse,
  BalanceSheetResponse,
  TrialBalanceResponse,
  DominioMappingStatus,
  DominioExportPreview,
} from '../services/apiService';
import { useAuth } from '../hooks/useAuth';
import { AlertCircle, CheckCircle2, Save } from 'lucide-react';

type ReportType = 'dre' | 'balance-sheet' | 'trial-balance' | 'dominio';

const REPORT_LABELS: Record<ReportType, string> = {
  'dre': 'DRE — Demonstração do Resultado',
  'balance-sheet': 'Balanço Patrimonial',
  'trial-balance': 'Balancete de Verificação',
  'dominio': 'Exportar para Domínio Sistemas',
};

export default function ReportsPage() {
  const { canEditDominioMapping } = useAuth();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [reportType, setReportType] = useState<ReportType>('dre');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dre, setDre] = useState<DREReportResponse | null>(null);
  const [balanceSheet, setBalanceSheet] = useState<BalanceSheetResponse | null>(null);
  const [trialBalance, setTrialBalance] = useState<TrialBalanceResponse | null>(null);
  const [dominioMapping, setDominioMapping] = useState<DominioMappingStatus | null>(null);
  const [dominioPreview, setDominioPreview] = useState<DominioExportPreview | null>(null);
  const [exporting, setExporting] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    const begin = `${year}-${String(month).padStart(2, '0')}-01`;
    const endMonth = month === 12 ? 1 : month + 1;
    const endYear = month === 12 ? year + 1 : year;
    const end = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;
    const period = `${year}-${String(month).padStart(2, '0')}`;

    (async () => {
      try {
        if (reportType === 'dre') {
          const data = await getDREReport({ begin, end });
          if (!cancelled) setDre(data);
        } else if (reportType === 'balance-sheet') {
          const data = await getBalanceSheet({ date: end });
          if (!cancelled) setBalanceSheet(data);
        } else if (reportType === 'dominio') {
          const [mapping, preview] = await Promise.all([
            getDominioMapping(),
            getDominioExportPreview({ begin, end }),
          ]);
          if (!cancelled) { setDominioMapping(mapping); setDominioPreview(preview); }
        } else {
          const data = await getTrialBalance({ period });
          if (!cancelled) setTrialBalance(data);
        }
      } catch (err: unknown) {
        if (!cancelled) setError(err instanceof Error ? err.message : 'Erro ao carregar relatório');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [year, month, reportType, reloadKey]);

  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const begin = `${year}-${String(month).padStart(2, '0')}-01`;
      const endMonth = month === 12 ? 1 : month + 1;
      const endYear = month === 12 ? year + 1 : year;
      const end = `${endYear}-${String(endMonth).padStart(2, '0')}-01`;
      const period = `${year}-${String(month).padStart(2, '0')}`;

      if (reportType === 'dominio') {
        await downloadDominioExport({ begin, end });
      } else if (reportType === 'dre') {
        await downloadReportPDF('dre', { begin, end });
      } else if (reportType === 'balance-sheet') {
        await downloadReportPDF('balance-sheet', { date: end });
      } else {
        await downloadReportPDF('trial-balance', { period });
      }
    } catch {
      setError('Erro ao exportar');
    } finally {
      setExporting(false);
    }
  }, [year, month, reportType]);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <PeriodSelector
          year={year}
          month={month}
          onChange={(y, m) => { setYear(y); setMonth(m); }}
        />
        <select
          value={reportType}
          onChange={(e) => setReportType(e.target.value as ReportType)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          {Object.entries(REPORT_LABELS).map(([key, label]) => (
            <option key={key} value={key}>{label}</option>
          ))}
        </select>
        <button
          onClick={handleExport}
          disabled={exporting || loading}
          className="text-sm border border-gray-300 rounded-lg px-3 py-2 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {exporting ? 'Exportando...' : reportType === 'dominio' ? 'Baixar TXT' : 'Exportar PDF'}
        </button>
        {loading && <span className="text-xs text-gray-400">Carregando...</span>}
      </div>

      {/* Content */}
      {loading && !dre && !balanceSheet && !trialBalance ? (
        <LoadingSpinner message="Carregando relatório..." />
      ) : error ? (
        <EmptyState title="Erro" description={error} />
      ) : (
        <>
          {reportType === 'dre' && dre && <DREReport data={dre} />}
          {reportType === 'balance-sheet' && balanceSheet && <BalanceSheetReport data={balanceSheet} />}
          {reportType === 'trial-balance' && trialBalance && <TrialBalanceReport data={trialBalance} />}
          {reportType === 'dominio' && dominioMapping && dominioPreview && (
            <DominioExportReport
              mapping={dominioMapping}
              preview={dominioPreview}
              canEditMapping={canEditDominioMapping}
              onMappingUpdated={() => setReloadKey((k) => k + 1)}
            />
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// DRE Component
// ---------------------------------------------------------------------------

function DREReport({ data }: { data: DREReportResponse }) {
  return (
    <Card>
      <CardContent>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          Demonstração do Resultado do Exercício
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <tbody>
              <DRERow label="Receita Bruta" value={data.receita_bruta} bold />
              <DRERow label="(-) Impostos sobre Receita (ISS, PIS, COFINS)" value={-data.impostos_receita.total} indent />
              <DRERow label="(=) Receita Líquida" value={data.receita_liquida} bold highlight />

              {data.despesas_operacionais.categorias.map((cat) => (
                <DRERow
                  key={cat.account}
                  label={`(-) ${cat.label}`}
                  value={-cat.value}
                  indent
                />
              ))}
              <DRERow label="(-) Total Despesas Operacionais" value={-data.despesas_operacionais.total} bold />
              <DRERow label="(=) Lucro Operacional" value={data.lucro_operacional} bold highlight />

              <DRERow label="(-) IRPJ + CSLL" value={-data.irpj_csll} indent />
              <DRERow label="(=) Lucro Líquido" value={data.lucro_liquido} bold highlight />
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

function DRERow({
  label,
  value,
  bold,
  indent,
  highlight,
}: {
  label: string;
  value: number;
  bold?: boolean;
  indent?: boolean;
  highlight?: boolean;
}) {
  const isNegative = value < 0;
  return (
    <tr className={`border-b ${highlight ? 'border-gray-300 bg-gray-50' : 'border-gray-100'}`}>
      <td className={`py-2.5 px-3 ${indent ? 'pl-8 text-gray-600' : ''} ${bold ? 'font-semibold' : ''}`}>
        {label}
      </td>
      <td className={`py-2.5 px-3 text-right font-mono ${bold ? 'font-semibold' : ''} ${isNegative ? 'text-red-600' : ''}`}>
        {formatBRL(Math.abs(value))}
        {isNegative && <span className="text-red-400 ml-1 text-xs">D</span>}
      </td>
    </tr>
  );
}

// ---------------------------------------------------------------------------
// Balance Sheet Component
// ---------------------------------------------------------------------------

function BalanceSheetReport({ data }: { data: BalanceSheetResponse }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Ativos */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Ativos
          </h3>
          <AccountList contas={data.ativos.contas} />
          <div className="border-t-2 border-gray-300 mt-2 pt-2 flex justify-between font-semibold text-sm">
            <span>Total Ativos</span>
            <span className="font-mono">{formatBRL(data.ativos.total)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Passivo + Patrimônio */}
      <div className="space-y-6">
        <Card>
          <CardContent>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Passivo
            </h3>
            <AccountList contas={data.passivo.contas} />
            <div className="border-t-2 border-gray-300 mt-2 pt-2 flex justify-between font-semibold text-sm">
              <span>Total Passivo</span>
              <span className="font-mono">{formatBRL(Math.abs(data.passivo.total))}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
              Patrimônio Líquido
            </h3>
            <AccountList contas={data.patrimonio_liquido.contas} />
            <div className="border-t-2 border-gray-300 mt-2 pt-2 flex justify-between font-semibold text-sm">
              <span>Total Patrimônio Líquido</span>
              <span className="font-mono">{formatBRL(Math.abs(data.patrimonio_liquido.total))}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function AccountList({ contas }: { contas: { label: string; value: number }[] }) {
  if (contas.length === 0) {
    return <p className="text-sm text-gray-400 italic">Nenhuma conta neste grupo</p>;
  }
  return (
    <div className="space-y-1">
      {contas.map((c) => (
        <div key={c.label} className="flex justify-between text-sm py-1 px-1">
          <span className="text-gray-700">{c.label}</span>
          <span className="font-mono">{formatBRL(Math.abs(c.value))}</span>
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Trial Balance Component
// ---------------------------------------------------------------------------

function TrialBalanceReport({ data }: { data: TrialBalanceResponse }) {
  return (
    <Card>
      <CardContent>
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
          Balancete de Verificação — {data.periodo}
        </h3>
        {data.contas.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Conta</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Débito</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Crédito</th>
                  <th className="text-right py-2 px-3 font-medium text-gray-500">Saldo</th>
                </tr>
              </thead>
              <tbody>
                {data.contas.map((c) => (
                  <tr key={c.account} className="border-b border-gray-100 hover:bg-blue-50 even:bg-gray-100">
                    <td className="py-2 px-3 text-gray-700">{c.account}</td>
                    <td className="py-2 px-3 text-right font-mono">
                      {c.debito > 0 ? formatBRL(c.debito) : '—'}
                    </td>
                    <td className="py-2 px-3 text-right font-mono">
                      {c.credito > 0 ? formatBRL(c.credito) : '—'}
                    </td>
                    <td className={`py-2 px-3 text-right font-mono ${c.saldo < 0 ? 'text-red-600' : ''}`}>
                      {formatBRL(Math.abs(c.saldo))}
                      <span className="text-gray-400 ml-1 text-xs">{c.saldo >= 0 ? 'D' : 'C'}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-300">
                  <td className="py-2 px-3 font-semibold">Total</td>
                  <td className="py-2 px-3 text-right font-mono font-semibold">{formatBRL(data.total_debito)}</td>
                  <td className="py-2 px-3 text-right font-mono font-semibold">{formatBRL(data.total_credito)}</td>
                  <td className="py-2 px-3 text-right font-mono font-semibold">—</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <EmptyState title="Sem dados" description="Nenhuma movimentação no período" />
        )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Domínio Sistemas Export Component
// ---------------------------------------------------------------------------

function DominioExportReport({
  mapping,
  preview,
  canEditMapping,
  onMappingUpdated,
}: {
  mapping: DominioMappingStatus;
  preview: DominioExportPreview;
  canEditMapping: boolean;
  onMappingUpdated: () => void;
}) {
  const [saving, setSaving] = useState(false);
  const [codes, setCodes] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const m of mapping.mapped) {
      initial[m.account] = m.dominio_code;
    }
    for (const acct of mapping.unmapped) {
      initial[acct] = '';
    }
    for (const acct of preview.unmapped_accounts) {
      if (!(acct in initial)) initial[acct] = '';
    }
    return initial;
  });

  // Sync codes when mapping changes externally
  React.useEffect(() => {
    const updated: Record<string, string> = {};
    for (const m of mapping.mapped) {
      updated[m.account] = m.dominio_code;
    }
    for (const acct of mapping.unmapped) {
      updated[acct] = '';
    }
    for (const acct of preview.unmapped_accounts) {
      if (!(acct in updated)) updated[acct] = '';
    }
    setCodes(updated);
  }, [mapping, preview]);

  // Check if there are unsaved changes
  const hasChanges = React.useMemo(() => {
    const originalMap: Record<string, string> = {};
    for (const m of mapping.mapped) {
      originalMap[m.account] = m.dominio_code;
    }
    for (const [acct, code] of Object.entries(codes)) {
      const original = originalMap[acct] || '';
      if (code !== original) return true;
    }
    return false;
  }, [codes, mapping]);

  const handleSave = async () => {
    const changes: Record<string, string> = {};
    const originalMap: Record<string, string> = {};
    for (const m of mapping.mapped) {
      originalMap[m.account] = m.dominio_code;
    }
    for (const [acct, code] of Object.entries(codes)) {
      const original = originalMap[acct] || '';
      if (code !== original) {
        changes[acct] = code;
      }
    }
    if (Object.keys(changes).length === 0) return;

    setSaving(true);
    try {
      await updateDominioMapping(changes);
      onMappingUpdated();
    } catch (err) {
      console.error('Failed to save mapping:', err);
      alert('Erro ao salvar mapeamento');
    } finally {
      setSaving(false);
    }
  };

  const accountsWithTransactions = React.useMemo(
    () => new Set([...preview.unmapped_accounts, ...preview.mapped_accounts_used]),
    [preview],
  );

  const allAccounts = Array.from(new Set([
    ...mapping.mapped.map((m) => m.account),
    ...mapping.unmapped,
    ...preview.unmapped_accounts,
  ])).sort();

  return (
    <div className="space-y-6">
      {/* Export Stats */}
      <Card>
        <CardContent>
          <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4">
            Resumo da Exportação
          </h3>
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-gray-800">{preview.total}</p>
              <p className="text-xs text-gray-500">Transações no período</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4 text-center">
              <p className="text-2xl font-bold text-green-700">{preview.exported}</p>
              <p className="text-xs text-gray-500">Exportáveis</p>
            </div>
            <div className={`rounded-lg p-4 text-center ${preview.skipped > 0 ? 'bg-amber-50' : 'bg-gray-50'}`}>
              <p className={`text-2xl font-bold ${preview.skipped > 0 ? 'text-amber-700' : 'text-gray-400'}`}>{preview.skipped}</p>
              <p className="text-xs text-gray-500">Sem mapeamento</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Mapping */}
      <Card>
        <CardContent>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Mapeamento de Contas ({mapping.total_mapped}/{mapping.total_accounts})
            </h3>
            {canEditMapping && hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 px-3 py-1.5 rounded-lg disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500 mb-3">
            <CheckCircle2 className="h-3 w-3 text-green-600 inline-block mr-1" />exportável no período selecionado
            <span className="mx-2">·</span>
            <AlertCircle className="h-3 w-3 text-amber-600 inline-block mr-1" />precisa de código
            <span className="mx-2">·</span>
            <span className="text-gray-400">contas sem movimentação no período</span>
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-3 font-medium text-gray-500">Conta Ledger</th>
                  <th className="text-left py-2 px-3 font-medium text-gray-500 w-48">Código Domínio</th>
                </tr>
              </thead>
              <tbody>
                {allAccounts.map((acct) => {
                  const hasTransaction = accountsWithTransactions.has(acct);
                  const hasMapped = !!codes[acct];
                  return (
                    <tr key={acct} className={`border-b border-gray-100 even:bg-gray-50 ${hasTransaction && !hasMapped ? 'bg-amber-50/50' : ''}`}>
                      <td className="py-1.5 px-3 font-mono text-xs">
                        {hasTransaction && hasMapped && (
                          <CheckCircle2 className="h-3.5 w-3.5 text-green-600 inline-block mr-1" aria-label="tem transações neste mês" />
                        )}
                        {hasTransaction && !hasMapped && (
                          <AlertCircle className="h-3.5 w-3.5 text-amber-600 inline-block mr-1" aria-label="tem transações mas falta código" />
                        )}
                        <span className={!hasTransaction ? 'text-gray-400' : 'text-gray-700'}>{acct}</span>
                      </td>
                      <td className="py-1.5 px-3">
                        <input
                          type="text"
                          value={codes[acct] || ''}
                          onChange={(e) => setCodes({ ...codes, [acct]: e.target.value })}
                          disabled={!canEditMapping || saving}
                          className={`w-full border rounded px-2 py-1 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            !codes[acct] ? 'border-amber-300 bg-amber-50' : 'border-gray-300'
                          } disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500`}
                          placeholder="Ex: 101"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
